import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Phase 2B — bounded, company-scoped, real-time taskId → invoices[] map for a
 * visible set of jobs. Used to DERIVE job financial state (invoices remain the
 * source of truth; nothing is written to the job).
 *
 * - NO N+1: does NOT create a listener per job row. It chunks the visible
 *   taskIds into Firestore `in` queries (max 30 values each) and attaches one
 *   listener per chunk (so a visible set of ~100 jobs = ~4 listeners).
 * - Strictly tenant-scoped to companies/{companyId}/invoices. No global query,
 *   no collectionGroup. A same-taskId invoice under a foreign company lives on
 *   a different path and is never read here.
 * - Real-time: invoice status changes (sent → paid, paid → refunded, etc.)
 *   propagate without a page reload.
 * - Listeners are torn down + rebuilt when the company or the visible taskId
 *   set changes; cleaned up on unmount.
 *
 * @param {string} companyId
 * @param {string[]} taskIds  visible job ids to resolve financial state for
 * @returns {Record<string, object[]>} map of taskId → matching invoices
 */
export function useJobInvoicesMap(companyId, taskIds) {
  // Stable, de-duped, sorted key so listeners only rebuild on real changes.
  const stableIds = useMemo(() => {
    const uniq = Array.from(new Set((taskIds || []).filter(Boolean)));
    uniq.sort();
    return uniq;
  }, [taskIds]);
  const idsKey = stableIds.join(',');

  const [map, setMap] = useState({});

  useEffect(() => {
    if (!companyId || stableIds.length === 0) {
      setMap({});
      return undefined;
    }

    const chunks = [];
    for (let i = 0; i < stableIds.length; i += 30) {
      chunks.push(stableIds.slice(i, i + 30));
    }

    // Per-chunk accumulator merged into a single taskId → invoices[] map.
    const perChunk = chunks.map(() => ({}));
    const rebuild = () => {
      const merged = {};
      perChunk.forEach((chunkMap) => {
        Object.entries(chunkMap).forEach(([tid, invs]) => {
          merged[tid] = (merged[tid] || []).concat(invs);
        });
      });
      setMap(merged);
    };

    const invoicesCol = collection(db, 'companies', companyId, 'invoices');
    const unsubs = chunks.map((chunk, idx) => {
      const q = query(invoicesCol, where('taskId', 'in', chunk));
      return onSnapshot(
        q,
        (snap) => {
          const byTask = {};
          snap.forEach((d) => {
            const data = { invoiceId: d.id, ...(d.data() || {}) };
            const tid = data.taskId;
            if (!tid) return;
            (byTask[tid] = byTask[tid] || []).push(data);
          });
          perChunk[idx] = byTask;
          rebuild();
        },
        (err) => console.warn('[useJobInvoicesMap] listener error:', err),
      );
    });

    return () => unsubs.forEach((u) => u && u());
  }, [companyId, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return map;
}
